import { isServer } from '@/helpers/platform';
import { SplitFeatureFlags } from '@/stores/splitFeatureFlags';
import { defaultSmgSplitSession } from '@/stores/useSplitStore';
import { FLAGSET_PROPERTY_VALUATION } from '@/types';
import { captureError } from '@smg-real-estate/browser-rum';
import {
  createClientCookieValue,
  getFeatureFlags,
  parseConsentCookie,
  readCookieValue,
  setDocumentCookie,
  type SmgSplitSession,
  SplitSessionCookieKey,
  SplitSupportedEnvironment,
  SplitSupportedPlatform,
} from '@smg-real-estate/vue-experiments';

const getSplitSupportedEnv = (): SplitSupportedEnvironment => {
  if (isServer()) {
    return process.env.VUE_STAGE === 'prod'
      ? SplitSupportedEnvironment.PROD
      : SplitSupportedEnvironment.TEST;
  } else {
    return import.meta.env.VUE_STAGE === 'prod'
      ? SplitSupportedEnvironment.PROD
      : SplitSupportedEnvironment.TEST;
  }
};

export async function fetchSmgSplitSession(): Promise<SmgSplitSession | undefined> {
  if (Object.keys(SplitFeatureFlags).length === 0) {
    return defaultSmgSplitSession;
  }

  try {
    const supportedPlatform = isServer()
      ? (process.env.VUE_APP_PLATFORM as SplitSupportedPlatform)
      : (import.meta.env.VUE_APP_PLATFORM as SplitSupportedPlatform);

    const featureFlagsResponse = await getFeatureFlags(['global', FLAGSET_PROPERTY_VALUATION], {
      platform: supportedPlatform,
      env: getSplitSupportedEnv(),
    });

    const { userKey, treatmentsWithConfig } = featureFlagsResponse.data.data;
    return {
      splitSessionKey: userKey,
      splitTreatmentsWithConfig: treatmentsWithConfig,
    };
  } catch (e) {
    captureError(e);
    return undefined;
  }
}

export const setTemporaryUUIDCookie = (
  splitSessionKey: string,
  documentCookie: string,
  locationHref: string,
): void => {
  const consentCookieValue = readCookieValue(documentCookie, 'OptanonConsent');
  const consentCookie = parseConsentCookie(consentCookieValue);
  if (!consentCookie?.groups.includes('C0002:1')) {
    return;
  }

  const cookieDomain = new URL(locationHref).host.split('.').slice(1).join('.');
  const tuuidCookie = `${createClientCookieValue(
    SplitSessionCookieKey.TEMP_SPLIT_SESSION_COOKIE_KEY,
    splitSessionKey,
  )} domain=.${cookieDomain}; secure; SameSite=None;`;
  setDocumentCookie(tuuidCookie);
};
